import { useState, useMemo, useEffect } from "react";
import PageLayout from "@/components/feature/PageLayout";
import { hallOfFamePlayers, type HallOfFamePlayer } from "@/mocks/formerPlayers";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { getHallOfFamePlayers } from "@/services/content";

function PlayerCard({ player }: { player: HallOfFamePlayer }) {
  return (
    <div className="group bg-[#121212] border border-[#232323] rounded-lg overflow-hidden transition-all duration-300 hover:border-[#F5C518] hover:-translate-y-1">
      <div className="relative aspect-square overflow-hidden bg-[#0a0a0a]">
        <img
          src={player.image}
          alt={`${player.name} — ${player.college}`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
      </div>
      <div className="p-4">
        <h3 className="font-heading text-white text-sm font-bold uppercase tracking-wide truncate">
          {player.name}
        </h3>
        <p className="font-body text-[#F5C518] text-xs font-semibold mt-1.5 leading-snug">
          {player.college}
        </p>
        <p className="font-body text-white/40 text-xs mt-0.5">
          {player.highSchool || "—"}
        </p>
      </div>
    </div>
  );
}

export default function FormerPlayersPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;
  // Falls back to src/mocks/formerPlayers.ts until Supabase is wired up.
  const { data: players } = useSupabaseData(getHallOfFamePlayers, hallOfFamePlayers);

  const filteredPlayers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return players;
    return players.filter(
      (player) =>
        player.name.toLowerCase().includes(q) ||
        player.college.toLowerCase().includes(q) ||
        player.highSchool.toLowerCase().includes(q)
    );
  }, [searchQuery, players]);

  const totalPages = Math.max(1, Math.ceil(filteredPlayers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedPlayers = filteredPlayers.slice(pageStart, pageStart + PAGE_SIZE);

  // Reset to page 1 when the search changes
  useEffect(() => setPage(1), [searchQuery]);

  // Build compact page-number list with ellipses
  const pageNumbers = useMemo<(number | "…")[]>(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set<number>([1, 2, totalPages - 1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const nums = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out: (number | "…")[] = [];
    nums.forEach((n, i) => {
      if (i > 0 && n - (nums[i - 1] as number) > 1) out.push("…");
      out.push(n);
    });
    return out;
  }, [totalPages, currentPage]);

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="w-full bg-[#0a0a0a] pt-28 md:pt-36 pb-10 md:pb-14">
        <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-px bg-[#F5C518]" />
            <span className="font-body text-[#F5C518] text-xs font-semibold tracking-[0.25em] uppercase">
              Triumph Basketball
            </span>
          </div>

          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[0.95]">
            <span className="text-white">Hall of </span>
            <span className="text-[#F5C518]">Fame</span>
          </h1>

          <p className="font-body text-white/50 text-sm md:text-base max-w-2xl mt-5 leading-relaxed">
            Elite athletes who came through Triumph Basketball and went on to compete
            at the collegiate level. Each player represents years of dedication,
            discipline, and the relentless pursuit of excellence.
          </p>
        </div>
      </section>

      {/* Search Bar */}
      <section className="w-full bg-[#0a0a0a] border-t border-[#232323] py-6 md:py-8 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="relative max-w-md">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-white/30 text-base" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, school, or university..."
              className="w-full bg-[#121212] border border-[#232323] rounded-lg pl-11 pr-4 py-2.5 font-body text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#F5C518] transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-white/30 hover:text-white/60"
                aria-label="Clear search"
              >
                <i className="ri-close-line text-base" />
              </button>
            )}
          </div>

        </div>
      </section>

      {/* Players Grid */}
      <section className="w-full bg-[#0a0a0a] py-10 md:py-14 pb-20 md:pb-28">
        <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
          {filteredPlayers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#121212] border border-[#232323] mb-5">
                <i className="ri-search-line text-white/20 text-2xl" />
              </div>
              <p className="font-heading text-white/40 text-lg font-semibold uppercase tracking-wide">
                No players found
              </p>
              <p className="font-body text-white/25 text-sm mt-2">
                Try adjusting your search
              </p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-5 font-body text-sm text-[#F5C518] hover:text-white transition-colors underline underline-offset-2"
              >
                Clear search
              </button>
            </div>
          ) : (
            <>
              <div
                className="grid gap-4 md:gap-5"
                style={{
                  gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
                }}
              >
                {paginatedPlayers.map((player) => (
                  <PlayerCard key={player.id} player={player} />
                ))}
              </div>

              {totalPages > 1 && (
                <nav
                  aria-label="Pagination"
                  className="mt-10 md:mt-14 flex flex-wrap items-center justify-center gap-2"
                >
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 font-body text-xs font-semibold uppercase tracking-wider bg-[#121212] border border-[#232323] text-white/70 hover:border-[#F5C518] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#232323] disabled:hover:text-white/70"
                    aria-label="Previous page"
                  >
                    <span className="hidden sm:inline">Prev</span>
                    <i className="ri-arrow-left-s-line sm:hidden text-base align-middle" />
                  </button>

                  {pageNumbers.map((n, i) =>
                    n === "…" ? (
                      <span
                        key={`e-${i}`}
                        className="px-2 font-body text-xs text-white/30"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={n}
                        onClick={() => setPage(n)}
                        aria-current={n === currentPage ? "page" : undefined}
                        className={`min-w-[36px] px-3 py-2 font-body text-xs font-semibold tracking-wider border transition-colors ${
                          n === currentPage
                            ? "bg-[#F5C518] border-[#F5C518] text-black"
                            : "bg-[#121212] border-[#232323] text-white/70 hover:border-[#F5C518] hover:text-white"
                        }`}
                      >
                        {n}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 font-body text-xs font-semibold uppercase tracking-wider bg-[#121212] border border-[#232323] text-white/70 hover:border-[#F5C518] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#232323] disabled:hover:text-white/70"
                    aria-label="Next page"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <i className="ri-arrow-right-s-line sm:hidden text-base align-middle" />
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>
    </PageLayout>
  );
}